import React from 'react';
import { Box, styled, ToggleButton, Tooltip, Typography } from '@mui/material';
import { useAppSelector } from 'store/hooks';
import { StyledToggleButtonGroup } from 'components/reviews/analytics/StyledToggleButtonGroup';

const PREVIEW_SIZE_PX = 160;

// Android adaptive icon spec - the full image is 108dp (432px), and each
// vendor masks/crops a different visible area out of it. `safeZoneDp` below is the
// vendor-specific visible centre that we render at 100% of the preview container; the
// remaining outer ring of the source image overflows and gets cropped by the mask.
const FULL_ICON_DP = 108;

type MaskType = 'circle' | 'rounded';

interface MaskConfig {
    borderRadius: string;
    safeZoneDp: number;
}

const MASKS: Record<MaskType, MaskConfig> = {
    circle: { borderRadius: '50%', safeZoneDp: 66 },
    rounded: { borderRadius: '30%', safeZoneDp: 72 }
};

const PreviewLayer = styled('img')({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.2s ease, height 0.2s ease'
});

export const AndroidAppDrawerIconPreview: React.FC = () => {
    const { tenantId } = useAppSelector(state => state.auth.tenant);
    const [foregroundLoaded, setForegroundLoaded] = React.useState(true);
    const [backgroundLoaded, setBackgroundLoaded] = React.useState(true);
    const [maskType, setMaskType] = React.useState<MaskType>('circle');

    const [mountTimestamp] = React.useState(() => Date.now());

    const baseForegroundUrl = `${
        import.meta.env.MEDIA_SERVICE_URL
    }/tenants/${tenantId}/app_assets/android_icon_foreground`;
    const baseBackgroundUrl = `${
        import.meta.env.MEDIA_SERVICE_URL
    }/tenants/${tenantId}/app_assets/android_icon_background`;

    const foregroundSrc = `${baseForegroundUrl}?t=${mountTimestamp}`;
    const backgroundSrc = `${baseBackgroundUrl}?t=${mountTimestamp}`;

    const handleMaskChange = React.useCallback((_: unknown, value: MaskType | null) => {
        if (value) {
            setMaskType(value);
        }
    }, []);
    const handleForegroundError = React.useCallback(() => setForegroundLoaded(false), []);
    const handleBackgroundError = React.useCallback(() => setBackgroundLoaded(false), []);

    const showFullPreview = foregroundLoaded && backgroundLoaded;
    const showPlaceholder = !showFullPreview;

    const mask = MASKS[maskType];
    const scalePercent = (FULL_ICON_DP / mask.safeZoneDp) * 100;

    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
            <Box
                width={PREVIEW_SIZE_PX}
                height={PREVIEW_SIZE_PX}
                position="relative"
                sx={{
                    borderRadius: mask.borderRadius,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: showFullPreview ? 'transparent' : 'grey.100',
                    transition: 'border-radius 0.2s ease'
                }}
            >
                {showFullPreview && (
                    <>
                        <PreviewLayer
                            src={backgroundSrc}
                            alt="background-preview"
                            onError={handleBackgroundError}
                            sx={{
                                width: `${scalePercent}%`,
                                height: `${scalePercent}%`,
                                objectFit: 'cover'
                            }}
                        />
                        <PreviewLayer
                            src={foregroundSrc}
                            alt="foreground-preview"
                            onError={handleForegroundError}
                            sx={{
                                width: `${scalePercent}%`,
                                height: `${scalePercent}%`,
                                objectFit: 'contain'
                            }}
                        />
                    </>
                )}
                {showPlaceholder && (
                    <Tooltip title="Please upload both images in order for preview to appear.">
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            height="100%"
                        >
                            <Typography variant="caption" color="text.secondary">
                                No preview
                            </Typography>
                        </Box>
                    </Tooltip>
                )}
            </Box>
            <StyledToggleButtonGroup value={maskType} exclusive size="small" onChange={handleMaskChange}>
                <ToggleButton value="circle">Circle</ToggleButton>
                <ToggleButton value="rounded">Rounded</ToggleButton>
            </StyledToggleButtonGroup>
        </Box>
    );
};
